import React, { useEffect, useRef, useState } from "react";
import { Typography } from "@mui/material";
import type { UploadSessionStatus } from "../types";

interface IntakeClockProps {
  startedAt: number | null;
  sessionStatus: UploadSessionStatus | null;
  stopped?: boolean;
}

const TERMINAL: UploadSessionStatus[] = ["COMPLETED", "FAILED"];

function glyphFor(status: UploadSessionStatus | null): string {
  if (status === "COMPLETED") return "✓";
  if (status === "FAILED") return "!";
  if (status === "PROCESSING" || status === "SUBMITTED") return "⟳";
  return "↑";
}

function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const IntakeClock: React.FC<IntakeClockProps> = ({ startedAt, sessionStatus, stopped }) => {
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!startedAt) {
      setElapsed(0);
      return;
    }

    const tick = () => setElapsed(Date.now() - startedAt);
    tick();

    const isTerminal = (sessionStatus && TERMINAL.includes(sessionStatus)) || stopped;
    if (!isTerminal) {
      intervalRef.current = setInterval(tick, 1000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startedAt, sessionStatus, stopped]);

  if (!startedAt) return null;

  const glyph = glyphFor(sessionStatus);

  return (
    <Typography
      component="span"
      sx={{
        fontFamily: "'Roboto Mono', 'ui-monospace', monospace",
        fontFeatureSettings: '"tnum"',
        fontSize: "12px",
        fontWeight: 400,
        color: "rgba(0,0,0,0.45)",
        letterSpacing: "0.02em",
        userSelect: "none",
      }}
    >
      {glyph} {formatElapsed(elapsed)}
    </Typography>
  );
};

export default IntakeClock;
